import { createContext, useContext, useEffect, useState } from "react"
import type { ReactNode } from "react"
import type { Role, User } from "../types"

type AuthStatus = "loading" | "authenticated" | "unauthenticated"

type AuthContextValue = {
  user: User | null
  status: AuthStatus
  login: (email: string, password: string) => Promise<{ ok: boolean; message?: string }>
  logout: () => Promise<void>
  hasRole: (roles: Role[]) => boolean
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

const parseUser = async (response: Response): Promise<User | null> => {
  if (!response.ok) return null
  const data = (await response.json()) as { user?: User }
  return data.user ?? null
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null)
  const [status, setStatus] = useState<AuthStatus>("loading")

  useEffect(() => {
    let active = true
    const load = async () => {
      try {
        const response = await fetch("/api/auth/me")
        const resolvedUser = await parseUser(response)
        if (!active) return
        if (resolvedUser) {
          setUser(resolvedUser)
          setStatus("authenticated")
          return
        }
      } catch {
        // API unreachable — stay unauthenticated.
      }

      if (!active) return
      setUser(null)
      setStatus("unauthenticated")
    }

    load()
    return () => {
      active = false
    }
  }, [])

  const login = async (email: string, password: string) => {
    setStatus("loading")
    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      })
      const resolvedUser = await parseUser(response)
      if (resolvedUser) {
        setUser(resolvedUser)
        setStatus("authenticated")
        return { ok: true }
      }
      setUser(null)
      setStatus("unauthenticated")
      return { ok: false, message: "Invalid credentials." }
    } catch {
      setUser(null)
      setStatus("unauthenticated")
      return { ok: false, message: "Unable to reach the auth service." }
    }
  }

  const logout = async () => {
    try {
      await fetch("/api/auth/logout", { method: "POST" })
    } catch {
      // Ignore network failures on logout.
    }
    setUser(null)
    setStatus("unauthenticated")
  }

  const value: AuthContextValue = {
    user,
    status,
    login,
    logout,
    hasRole: (roles) => (user ? roles.includes(user.role) : false),
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider")
  }
  return context
}
